// src/components/InboxView.tsx
import React, { useState } from 'react';
import { Bell, Tag, Scissors, Trash2, ChevronDown, ChevronUp, Users, CheckCircle2 } from 'lucide-react';

interface InboxViewProps {
    inbox: any[];
    user: any;
    onProcessItem: (item: any) => void;
    onDeleteItem: (id: string) => void;
    onDirectProcessItem?: (item: any) => void; 
}

export default function InboxView({ inbox, user, onProcessItem, onDeleteItem, onDirectProcessItem }: InboxViewProps) {
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

    // Groepeer de inbox items op basis van telefoonnummer (of naam als fallback)
    const groupedInbox = inbox.reduce((acc, item) => {
        const key = item.phone || item.name || 'Onbekend';
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {} as Record<string, any[]>);

    const toggleGroup = (key: string) => {
        setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Async loop zodat hij netjes wacht tot racket 1 in de database staat voor hij racket 2 pakt
    const handleDirectProcessGroup = async (items: any[]) => {
        if (onDirectProcessItem) {
            for (const item of items) {
                await onDirectProcessItem(item);
            }
        }
    };

    const handleProcessGroup = (items: any[]) => {
        items.forEach(item => onProcessItem(item));
    };

    const handleDeleteGroup = (items: any[]) => {
        if (window.confirm(`Weet je zeker dat je alle ${items.length} aanvragen wilt verwijderen?`)) {
            items.forEach(item => onDeleteItem(item.id));
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-500 max-w-4xl mx-auto">
            <div className="flex items-center justify-between gap-4">
                <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center">
                    <Bell className="w-6 h-6 mr-3 text-indigo-600 dark:text-indigo-400" />
                    Nieuwe Aanvragen ({inbox.length})
                </h2>
            </div>
            
            <div className="space-y-4">
                {inbox.length === 0 ? (
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-10 text-center text-slate-500 dark:text-slate-400">
                        <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500/50" />
                        Helemaal bijgewerkt! Er staan geen nieuwe aanvragen klaar.
                    </div>
                ) : (
                    Object.keys(groupedInbox).map((key: string) => {
                        const items: any[] = groupedInbox[key];
                        const isExpanded = expandedGroups[key];
                        const isMultiple = items.length > 1;
                        const firstItem = items[0];

                        return (
                            <div key={key} className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden transition-all">
                                
                                {/* Groep Header (Bevat de globale info van de klant) */}
                                <div className="p-4 md:p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-50/50 dark:bg-slate-900/50">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center shrink-0">
                                            <Users className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-lg text-slate-800 dark:text-white flex items-center gap-2">
                                                {firstItem.name}
                                                {isMultiple && <span className="bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-full">{items.length} rackets</span>}
                                            </h3>
                                            <p className="text-sm text-slate-500 flex items-center gap-2">
                                                📞 {firstItem.phone} 
                                                <span className="opacity-50">•</span> 
                                                {new Date(firstItem.createdAt).toLocaleDateString('nl-NL')}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {/* COMPACTE KNOPPEN VOOR DE HELE GROEP */}
                                    {isMultiple && (
                                        <div className="flex items-center gap-2 mt-3 md:mt-0">
                                            <button 
                                                onClick={() => toggleGroup(key)} 
                                                className="p-2.5 bg-slate-200/50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition"
                                                title={isExpanded ? 'Verberg rackets' : 'Bekijk rackets'}
                                            >
                                                {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                            </button>
                                            
                                            <button 
                                                onClick={() => handleDeleteGroup(items)} 
                                                className="p-2.5 bg-slate-200/50 dark:bg-slate-800 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition"
                                                title="Verwijder hele groep"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>

                                            <button 
                                                onClick={() => handleProcessGroup(items)}
                                                className="px-4 py-2.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-900/60 rounded-lg text-sm font-bold transition"
                                                title="Controleer en verwerk alle rackets handmatig"
                                            >
                                                Verwerken ({items.length})
                                            </button>

                                            {onDirectProcessItem && (
                                                <button 
                                                    onClick={() => handleDirectProcessGroup(items)}
                                                    className="p-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition shadow-sm"
                                                    title="Direct allemaal opslaan"
                                                >
                                                    <CheckCircle2 className="w-5 h-5" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Individuele Rackets (Uitgeklapt) */}
                                {(isExpanded || !isMultiple) && (
                                    <div className="border-t border-slate-100 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700/50">
                                        {items.map((item: any, index: number) => (
                                            <div key={item.id} className="p-4 md:p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white dark:bg-slate-800">
                                                
                                                <div className="space-y-1 w-full md:w-auto">
                                                    {isMultiple && <p className="text-xs font-bold text-indigo-500 mb-2 uppercase tracking-wider">Racket {index + 1}</p>}
                                                    <p className="text-slate-800 dark:text-slate-200 font-medium flex items-center"><Tag className="w-4 h-4 mr-2 shrink-0 text-slate-400"/> {item.racket}</p>
                                                    <p className="text-sm text-slate-600 dark:text-slate-400 flex items-center mt-1">
                                                        <Scissors className="w-4 h-4 mr-2 shrink-0 text-slate-400"/> 
                                                        <span className="truncate">
                                                            {item.ownString && <span className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded text-[10px] uppercase font-bold mr-2">Eigen Snaar</span>}
                                                            {item.string || 'Geen snaar'} {item.isHybrid && item.crossString ? `/ ${item.crossString}` : ''} • {item.tensionMain || item.tension || '?'} / {item.tensionCross || item.tensionMain || item.tension || '?'} kg
                                                        </span>
                                                    </p>
                                                </div>

                                                {/* COMPACTE KNOPPEN VOOR LOS RACKET */}
                                                <div className="flex items-center gap-2 mt-3 md:mt-0">
                                                    <button 
                                                        onClick={() => onDeleteItem(item.id)} 
                                                        className="p-2.5 bg-slate-100 dark:bg-slate-900/80 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition"
                                                        title="Verwijder dit racket"
                                                    >
                                                        <Trash2 className="w-5 h-5" />
                                                    </button>
                                                    <button 
                                                        onClick={() => onProcessItem(item)}
                                                        className="px-4 py-2.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 rounded-lg text-sm font-bold transition"
                                                    >
                                                        Verwerken
                                                    </button>
                                                    {onDirectProcessItem && (
                                                        <button 
                                                            onClick={() => onDirectProcessItem(item)}
                                                            className="p-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition shadow-sm"
                                                            title="Direct goedkeuren en opslaan"
                                                        >
                                                            <CheckCircle2 className="w-5 h-5" /> 
                                                        </button>
                                                    )}
                                                </div>

                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}